// src/components/layout/MainLayout.jsx
//
// The app shell is the design's fixed 1536x1024 canvas: a flat single-tone backdrop
// (--app-backdrop in index.css — the client's chosen colour, a warm cream) fills the
// window, the sidebar floats at (20, 0) and the top bar sits on top of it. The whole
// canvas is scaled to the window with `zoom` on <body>, so every page renders at the
// design's proportions on any screen.
//
// Tablets and phones (narrower than COMPACT_MAX) get a flowing shell instead: a compact
// top bar with a menu button, the sidebar as a slide-in drawer, and pages at the full
// window width at their normal size (scaling the canvas there made text ~6px).
import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import useCompactLayout from '../../hooks/useCompactLayout';

export const CANVAS_W = 1536;
export const CANVAS_H = 1024;
export const CONTENT_LEFT = 255;   // first pixel of page content, right of the sidebar
export const CONTENT_TOP = 93;     // first pixel of page content, below the top bar
export const CONTENT_RIGHT = 45;   // canvas edge to content edge

const MainLayout = () => {
  const location = useLocation();
  const isDashboard = location.pathname === '/dashboard';
  const hideNavbar = location.pathname === '/designing-dashboard';
  const compact = useCompactLayout();
  const [menuOpen, setMenuOpen] = useState(false);

  // a page change (a menu link) closes the drawer
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // The canvas always fills the window's full WIDTH and is centred. Wide screens
  // (16:9 laptops, and especially 16:9 TVs up to ~55") have a wider aspect ratio
  // than the design's 1536x1024 (1.5:1), so fitting both dimensions (the old
  // behaviour) left large empty backdrop margins on either side instead of using
  // the screen. Filling the width instead means the canvas can run taller than
  // the viewport on very wide/short windows, in which case the page scrolls
  // vertically to reach the rest — a normal, minor trade-off compared to a TV
  // screen with 15-20% of its width sitting empty. Zoom lives on the canvas, not
  // <body>: menus and dialogs portal to <body> and position themselves from
  // getBoundingClientRect, which already accounts for the canvas zoom.
  const fit = () => Math.max(window.innerWidth / CANVAS_W, 0.5);
  const [zoom, setZoom] = useState(fit);
  useEffect(() => {
    const apply = () => setZoom(fit());
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, []);

  if (compact) {
    return (
      <div className="app-stage">
        <div className="app-canvas is-compact">
          <Navbar compact onMenu={() => setMenuOpen(true)} />
          {menuOpen && (
            <div
              aria-hidden="true"
              onClick={() => setMenuOpen(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(30, 10, 14, 0.45)', zIndex: 45 }}
            />
          )}
          <Sidebar drawer open={menuOpen} onClose={() => setMenuOpen(false)} />
          <main style={{ padding: '12px 12px 28px', minWidth: 0 }}>
            <Outlet />
          </main>
        </div>
      </div>
    );
  }

  // .app-stage's flat colour (--app-backdrop) covers the whole window; the UI sits on it.
  return (
    <div className="app-stage">
      <div className="app-canvas" style={{ zoom }}>
        <Sidebar />
        {!hideNavbar && <Navbar />}

        <main
          style={{
            paddingLeft: CONTENT_LEFT,
            paddingRight: CONTENT_RIGHT,
            paddingTop: hideNavbar ? 20 : CONTENT_TOP,
            paddingBottom: isDashboard ? 0 : 16,
            minHeight: CANVAS_H,
          }}
        >
          <Outlet />
        </main>

      </div>
    </div>
  );
};

export default MainLayout;
